import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, FileText, Loader2, CheckCircle2 } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/format";
import api from "@/lib/api";

const CATEGORY_LABELS = { k1: "K-1", call_notice: "Call notice", dist_notice: "Distribution notice", lpa: "LPA", report: "Report", other: "Document" };

export default function FundPositionDetail() {
  const { lpId } = useParams();
  const [data, setData] = useState(null);
  const [confirming, setConfirming] = useState("");

  const load = () => api.get(`/portal/fund-positions/${lpId}`).then(({ data }) => setData(data)).catch(() => toast.error("Could not load this position"));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [lpId]);

  const confirmPayment = async (itemId) => {
    setConfirming(itemId);
    try {
      await api.post(`/portal/capital-call-items/${itemId}/confirm`);
      toast.success("Thanks - your fund manager has been notified you sent payment");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not confirm"); }
    finally { setConfirming(""); }
  };

  const downloadDoc = async (d) => {
    try {
      const res = await api.get(`/portal/fund-docs/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.name || "document"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  if (!data) return <AppShell title="Fund Position"><p className="text-sm text-muted-foreground">Loading…</p></AppShell>;
  const { fund, account, capital_calls, distributions, documents, fund_metrics, gp_name } = data;
  const openCalls = capital_calls.filter((c) => c.status === "pending");

  return (
    <AppShell title={fund.name} subtitle={`Managed by ${gp_name} · ${String(fund.type).toUpperCase()}${fund.vintage_year ? ` · Vintage ${fund.vintage_year}` : ""}`}
      actions={<Link to="/portal/funds"><Button variant="outline" size="sm" className="gap-1.5" data-testid="lp-back-btn"><ArrowLeft className="h-3.5 w-3.5" /> All positions</Button></Link>}>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6" data-testid="lp-account-cards">
        {[["Commitment", formatCurrency(account.commitment)], ["Called", formatCurrency(account.called)], ["Paid-in", formatCurrency(account.contributed)],
          ["Unfunded", formatCurrency(account.unfunded)], ["Distributed", formatCurrency(account.distributed)], ["NAV share", formatCurrency(account.nav_share)]].map(([l, v]) => (
          <Card key={l} className="rounded-xl border p-4">
            <div className="text-sm font-bold text-foreground sm:text-base">{v}</div>
            <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{l}</div>
          </Card>
        ))}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Fund performance: TVPI {fund_metrics.tvpi != null ? `${fund_metrics.tvpi}x` : "-"} · DPI {fund_metrics.dpi != null ? `${fund_metrics.dpi}x` : "-"} · Net IRR {fund_metrics.irr != null ? `${(fund_metrics.irr * 100).toFixed(1)}%` : "-"} · Your share {account.share_pct}%</p>

      {openCalls.length > 0 && (
        <Card className="mt-6 rounded-xl border border-amber-200 bg-amber-50/50 p-5" data-testid="lp-open-calls">
          <h3 className="font-semibold text-amber-900">Open capital calls</h3>
          <div className="mt-3 space-y-2">
            {openCalls.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-white p-3" data-testid={`lp-call-${c.id}`}>
                <div>
                  <div className="text-sm font-semibold text-foreground">Call #{c.call_number} - {formatCurrency(c.amount)}</div>
                  <div className="text-xs text-muted-foreground">Due {c.due_date}{c.purpose ? ` · ${c.purpose}` : ""}</div>
                  {c.wire_instructions && <div className="mt-1 whitespace-pre-wrap text-xs text-slate-600">{c.wire_instructions}</div>}
                </div>
                {c.lp_marked_at ? (
                  <span className="flex items-center gap-1 text-xs font-medium text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Payment sent - awaiting confirmation</span>
                ) : (
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => confirmPayment(c.id)} disabled={confirming === c.id} data-testid={`lp-confirm-payment-${c.id}`}>
                    {confirming === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} I've sent payment
                  </Button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="rounded-xl border p-5" data-testid="lp-distributions">
          <h3 className="font-semibold text-foreground">Distributions</h3>
          {distributions.length === 0 ? <p className="mt-2 text-sm text-muted-foreground">No distributions yet.</p> : (
            <div className="mt-3 space-y-2">
              {distributions.map((d) => (
                <div key={d.id} className="flex items-center justify-between rounded-lg border p-3 text-sm">
                  <div>
                    <div className="font-medium text-foreground">#{d.dist_number} {d.source ? `· ${d.source}` : ""}</div>
                    <div className="text-xs text-muted-foreground">{d.date} · ROC {formatCurrency(d.return_of_capital)} · pref {formatCurrency(d.pref_return)} · profit {formatCurrency(d.profit)}</div>
                  </div>
                  <span className="font-bold text-emerald-700">{formatCurrency(d.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card className="rounded-xl border p-5" data-testid="lp-doc-vault">
          <h3 className="font-semibold text-foreground">Documents</h3>
          {documents.length === 0 ? <p className="mt-2 text-sm text-muted-foreground">No documents shared yet. K-1s, notices and the LPA will appear here.</p> : (
            <div className="mt-3 space-y-2">
              {documents.map((d) => (
                <div key={d.id} className="flex items-center justify-between rounded-lg border p-3 text-sm">
                  <div className="flex min-w-0 items-center gap-2">
                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <div className="truncate font-medium text-foreground">{d.name}</div>
                      <div className="text-xs text-muted-foreground">{CATEGORY_LABELS[d.category] || "Document"}{d.lp_id ? " · for you" : " · all LPs"}</div>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => downloadDoc(d)} data-testid={`lp-doc-download-${d.id}`}><Download className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
      <p className="mt-4 text-[11px] text-muted-foreground">Terms: {fund.mgmt_fee_pct}% management fee · {fund.carry_pct}% carry · {fund.hurdle_pct}% preferred return. Figures are unaudited.</p>
    </AppShell>
  );
}
